import { create } from 'zustand';
import { resolveErrorKey } from '@/infrastructure/util/error';
import type { Category } from '@/domain/model/Category';
import type { Subcategory } from '@/domain/model/Subcategory';
import { CatalogService } from '@/application/service/CatalogService';
import { CatalogApiAdapter } from '@/infrastructure/api/CatalogApiAdapter';

interface CatalogState {
  categories: Category[];
  currentCategory: Category | null;
  currentSubcategory: Subcategory | null;
  isLoading: boolean;
  error: string | null;
}

interface CatalogActions {
  fetchCategories: () => Promise<void>;
  fetchCategory: (id: string) => Promise<void>;
  fetchSubcategory: (id: string) => Promise<void>;
  clearCurrentCategory: () => void;
  clearCurrentSubcategory: () => void;
}

const catalogService = new CatalogService(new CatalogApiAdapter());

export const useCatalogStore = create<CatalogState & CatalogActions>((set) => ({
  categories: [],
  currentCategory: null,
  currentSubcategory: null,
  isLoading: false,
  error: null,

  fetchCategories: async () => {
    set({ isLoading: true, error: null });
    try {
      const categories = await catalogService.getCategories();
      set({ categories, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'category.loadFailed') });
    }
  },

  fetchCategory: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const category = await catalogService.getCategory(id);
      set({ currentCategory: category, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'category.loadFailed') });
    }
  },

  fetchSubcategory: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const subcategory = await catalogService.getSubcategory(id);
      set({ currentSubcategory: subcategory, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: resolveErrorKey(e, 'category.loadFailed') });
    }
  },

  clearCurrentCategory: () => set({ currentCategory: null }),

  clearCurrentSubcategory: () => set({ currentSubcategory: null }),
}));
